import { useState } from 'react'
import { navigate } from 'gatsby'
import {
  CustomerCreateInput,
  useCustomerCreateMutation,
} from 'lib/graphql/storeadmin'
import { SEO, showMessage } from 'components/common'
import { Editor } from 'components/customers'
import { customerErrorHandling } from 'lib/hooks'
import { DuplicateFieldsValue, getDuplicateErrorMessages } from './[id]/edit'
import AllowDuplicateModal from 'components/customers/modals/AllowDuplicateModal'

export const Head = () => <SEO title="顧客（法人・団体）の追加" />

const IndexPage = (): JSX.Element => {
  const [createCustomer, { loading, error }] = useCustomerCreateMutation()

  // Customer Corporate error useState
  const [showCustomerErrorAlert, setShowCustomerErrorAlert] = useState(false)
  const [customerErrorDescription, setCustomerErrorDescription] = useState([])

  // モーダルの表示・非表示
  const [showModal, setShowModal] = useState(false)
  // 重複可否を選択するモーダルのonOKの引数に渡すvaluesを一時的に保存するためのステート
  const [tmpValues, setTmpValues] = useState<CustomerCreateInput>(null)
  // 重複エラーのフィールド名を日本語の項目名に変換した文字列を格納するステート
  const [duplicateErrorMessages, setDuplicateErrorMessages] =
    useState<string[]>(null)

  const onFinishFailed = () => {
    showMessage('error', 'エラーが発生しました。')
  }

  // 重複可否選択モーダルのOKボタンをクリックしたときの処理
  const handleClickAllowDuplicate = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (tmpValues) onFinish(true, tmpValues)
    else showMessage('error', 'エラーが発生しました。')
    setShowModal(false)
  }

  const onFinish = async (
    allowDuplicate = false,
    values: CustomerCreateInput
  ) => {
    try {
      const { data } = await createCustomer({
        variables: { allowDuplicate, input: values },
      })
      const userErrors = data?.customerCreate?.customerUserErrors || []
      if (userErrors.length > 0) {
        // 重複エラーがある場合
        if (userErrors.some(({ code }) => code === 'DUPLICATE_DATA_EXISTS')) {
          // 重複エラーがある場合、重複可否を選択するモーダルを表示する
          setShowModal(true)

          // フォームの入力値を保存する
          // 重複可否モーダルで「登録」を実行した時に使用する
          setTmpValues(values)

          // 重複エラーのフィールド名を取得する
          const duplicateFields = userErrors
            .filter(({ code }) => code === 'DUPLICATE_DATA_EXISTS')
            .flatMap(({ field }) => field)

          // 重複可否モーダルに表示する重複した項目の入力値のオブジェクトを作成する
          const duplicateFieldsValues: DuplicateFieldsValue = {
            corporationName: values.corporationName,
            corporationNameKana: values.corporationNameKana,
            firstName: values.firstName,
            lastName: values.lastName,
            zip: values.address.zip,
            city: values.address.city,
            address1: values.address.address1,
            address2: values.address.address2,
            phone: values.phone,
          }

          // 重複エラーのフィールド名からエラーメッセージを生成して、ステートを更新
          setDuplicateErrorMessages(
            getDuplicateErrorMessages(duplicateFields, duplicateFieldsValues)
          )
        }
        // 重複エラー以外のエラーがある場合
        else {
          setShowCustomerErrorAlert(true)
          const errorMessages = customerErrorHandling(userErrors)
          setCustomerErrorDescription(errorMessages)
          showMessage('error', 'エラーが発生しました。')
        }
      } else if (error) {
        showMessage('error', 'エラーが発生しました。')
      } else {
        setShowCustomerErrorAlert(false)
        showMessage('success', '新規顧客を追加しました。')
        navigate(`../${data.customerCreate.customer.id}`)
      }
    } catch (e) {
      setShowCustomerErrorAlert(true)
      setCustomerErrorDescription([e.message])
      showMessage('error', 'サーバーとの通信時にエラーが発生しました。')
    }
  }
  const onCloseAlert = () => {
    setShowCustomerErrorAlert(false)
  }

  return (
    <>
      <Editor
        isNew={true}
        title="顧客（法人・団体）を追加する"
        type="corporate"
        requesting={loading}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        showAlert={showCustomerErrorAlert}
        onCloseAlert={onCloseAlert}
        alertDescription={customerErrorDescription}
      />
      {/* 重複可否選択モーダル */}
      <AllowDuplicateModal
        errorMessages={duplicateErrorMessages}
        onCancel={() => {
          setShowModal(false)
          setTmpValues(null)
        }}
        onOk={handleClickAllowDuplicate}
        okText="登録"
        open={showModal}
        loading={loading}
      />
    </>
  )
}

export default IndexPage
